import React, { useState } from 'react';
import {
  SunOutlined,
  MoonOutlined,
  SettingOutlined,
  CheckOutlined
} from '@ant-design/icons';
import { useTheme } from '../contexts/ThemeContext';
import { ThemeMode } from '../types/theme';

const ThemeToggle: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const getThemeIcon = (mode: ThemeMode) => {
    switch (mode) {
      case 'light':
        return <SunOutlined />;
      case 'dark':
        return <MoonOutlined />;
      case 'system':
        return <SettingOutlined />;
      default:
        return <SunOutlined />;
    }
  };

  const getThemeLabel = (mode: ThemeMode) => {
    switch (mode) {
      case 'light':
        return 'Light';
      case 'dark':
        return 'Dark';
      case 'system':
        return 'System';
      default:
        return 'Light';
    }
  };

  const handleThemeSelect = (selectedTheme: ThemeMode) => {
    setTheme(selectedTheme);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-9 w-9 items-center justify-center rounded-xl text-foreground-muted transition-colors hover:bg-background-tertiary hover:text-foreground"
        title={`Current theme: ${getThemeLabel(theme)}`}
        aria-label={`Theme: ${getThemeLabel(theme)}`}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        {getThemeIcon(theme)}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl shadow-md z-50">
            <div className="px-2 py-1">
              <button
                onClick={() => handleThemeSelect('light')}
                className="flex items-center justify-between w-full px-2 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <span className="flex items-center gap-2">
                  <SunOutlined />
                  Light
                </span>
                {theme === 'light' && <CheckOutlined className="text-blue-600" />}
              </button>
              <button
                onClick={() => handleThemeSelect('dark')}
                className="flex items-center justify-between w-full px-2 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <span className="flex items-center gap-2">
                  <MoonOutlined />
                  Dark
                </span>
                {theme === 'dark' && <CheckOutlined className="text-blue-600" />}
              </button>
              <button
                onClick={() => handleThemeSelect('system')}
                className="flex items-center justify-between w-full px-2 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <span className="flex items-center gap-2">
                  <SettingOutlined />
                  System
                </span>
                {theme === 'system' && <CheckOutlined className="text-blue-600" />}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ThemeToggle;
